"use client";

import * as React from "react";
import { createContextualCan } from "@casl/react";

import { useTenantAuthFlow } from "@/features/auth/providers/tenant-auth-flow-provider";
import { createAppAbility, createEmptyAppAbility } from "@/features/auth/casl/create-app-ability";
import type { AppAbility } from "@/features/auth/casl/types";
import { resolveWorkspaceAccessRecord } from "@/features/auth/lib/workspace-access-record";
import { useActiveWorkspaceSlug } from "@/features/auth/hooks/use-active-workspace-slug";
import { useAdminRouteSlugs } from "@/features/auth/hooks/use-admin-route-slugs";

const defaultAppAbility = createEmptyAppAbility();

export const AppAbilityContext = React.createContext<AppAbility>(defaultAppAbility);

export const Can = createContextualCan(AppAbilityContext.Consumer);

export function AppAbilityProvider({ children }: { children: React.ReactNode }) {
  const { authMe } = useTenantAuthFlow();
  const urlWorkspaceSlug = useAdminRouteSlugs().workspaceSlug;
  const storeWorkspaceSlug = useActiveWorkspaceSlug();
  // Prefer the URL workspace slug so the CASL ability matches what AdminModuleAccessGuard checks.
  // Fall back to the store slug on non-admin routes where the URL param is absent.
  const activeWorkspaceSlug = urlWorkspaceSlug ?? storeWorkspaceSlug;

  const ability = React.useMemo(() => {
    const record = resolveWorkspaceAccessRecord(authMe, activeWorkspaceSlug);
    return createAppAbility(record);
  }, [activeWorkspaceSlug, authMe]);

  return <AppAbilityContext.Provider value={ability}>{children}</AppAbilityContext.Provider>;
}

export function useAppAbility(): AppAbility {
  return React.useContext(AppAbilityContext);
}
